<!-- The one choice that decides everything below it. -->
<div class="bss-feature">
  <mat-icon aria-hidden="true">{{ actionIcon() }}</mat-icon>
  <app-design-control-renderer
    [control]="feature"
    [values]="appearance()"
    [options]="allOptions()"
    (valueChange)="onControlChange($event)"
  ></app-design-control-renderer>
</div>

<!-- ── Label & style (every button) ─────────────────────────────────────── -->
<div class="bss-group" *ngIf="!hideStyle()">
  <button type="button" class="bss-group-head" (click)="toggle('style')">
    <mat-icon>{{ isOpen('style') ? 'expand_more' : 'chevron_right' }}</mat-icon>
    <span>Label &amp; style</span>
  </button>
  <div class="bss-group-body" *ngIf="isOpen('style')">
    <app-design-control-renderer
      *ngFor="let control of styleControls; trackBy: trackByControlKey"
      [control]="control"
      [values]="appearance()"
      [options]="allOptions()"
      (valueChange)="onControlChange($event)"
    ></app-design-control-renderer>
  </div>
</div>

<!-- ── The picked feature's settings ────────────────────────────────────── -->
<div class="bss-group" *ngIf="config().action !== 'none'">
  <button type="button" class="bss-group-head" (click)="toggle('feature')">
    <mat-icon>{{ isOpen('feature') ? 'expand_more' : 'chevron_right' }}</mat-icon>
    <span>{{ featureTitle() }}</span>
  </button>

  <div class="bss-group-body" *ngIf="isOpen('feature')" [ngSwitch]="config().action">

    <!-- Download ---------------------------------------------------------- -->
    <ng-container *ngSwitchCase="'download'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['downloadSource'] }"></ng-container>

      <ng-container *ngIf="config().downloadSource === 'widget'">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['downloadWidget'] }"></ng-container>
        <p class="bss-hint" *ngIf="!widgetOptions().length">Add a chart or table to the canvas to download its data.</p>
      </ng-container>
      <ng-container *ngIf="config().downloadSource === 'excel'">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['downloadExcel'] }"></ng-container>
        <p class="bss-hint">
          The server exports <b>every row</b> of the dataset as .xlsx, filtered by what is selected on the dashboard right now
          (dropdowns and chart clicks). A selection on a column the dataset does not have is left out.
        </p>
      </ng-container>
      <ng-container *ngIf="config().downloadSource === 'api'">
        <ng-container *ngTemplateOutlet="downloadApi"></ng-container>
      </ng-container>
      <ng-template #downloadApi>
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['downloadApi'] }"></ng-container>
        <ng-container *ngIf="hasBody(config().downloadMethod)">
          <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'downloadBody', label: 'Body', value: config().downloadBody }"></ng-container>
        </ng-container>
        <p class="bss-hint">The response is saved as a file; its name comes from the server's <code>Content-Disposition</code> when set.</p>
      </ng-template>

      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['downloadName'] }"></ng-container>
    </ng-container>

    <!-- Call API ---------------------------------------------------------- -->
    <ng-container *ngSwitchCase="'api'">
      <div class="bss-card" *ngFor="let api of config().apis; let i = index; let first = first; let last = last; trackBy: trackByApi">
        <div class="bss-card-head">
          <input
            type="text"
            class="bss-card-name"
            [value]="api.name"
            (input)="setApi(i, { name: $any($event.target).value })"
            [attr.aria-label]="'Name of API ' + (i + 1)"
          />
          <button type="button" class="bss-icon" [disabled]="first" (click)="moveApi(i, -1)" aria-label="Move API up"><mat-icon>arrow_upward</mat-icon></button>
          <button type="button" class="bss-icon" [disabled]="last" (click)="moveApi(i, 1)" aria-label="Move API down"><mat-icon>arrow_downward</mat-icon></button>
          <button type="button" class="bss-icon is-danger" (click)="removeApi(i)" aria-label="Remove API"><mat-icon>close</mat-icon></button>
        </div>

        <label class="dsg-field dsg-field-wide">
          <span>Method</span>
          <select [ngModel]="api.method" (ngModelChange)="setApi(i, { method: $event })">
            <option *ngFor="let method of methods; trackBy: trackByValue" [ngValue]="method.value">{{ method.label }}</option>
          </select>
        </label>
        <label class="dsg-field dsg-field-wide">
          <span>URL</span>
          <input type="text" placeholder="/api/orders/approve" [ngModel]="api.url" (ngModelChange)="setApi(i, { url: $event })" />
        </label>
        <label class="dsg-field dsg-field-wide" *ngIf="hasBody(api.method)" [class.is-invalid]="isBadJson(api.body)">
          <span>Body</span>
          <textarea rows="4" spellcheck="false" [ngModel]="api.body" (ngModelChange)="setApi(i, { body: $event })"></textarea>
        </label>
        <label class="dsg-field dsg-field-wide" [class.is-invalid]="isBadJson(api.headers, false)">
          <span>Headers</span>
          <textarea rows="2" spellcheck="false" placeholder='{ "X-Source": "dashboard" }' [ngModel]="api.headers" (ngModelChange)="setApi(i, { headers: $event })"></textarea>
        </label>
      </div>

      <button type="button" class="bss-add" (click)="addApi()"><mat-icon>add</mat-icon> Add API</button>
      <p class="bss-hint" *ngIf="!config().apis.length">Add one or more APIs; they run when the button is clicked.</p>

      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['apiRun'] }"></ng-container>

      <div class="bss-subhead">Refresh with the response <small>optional</small></div>
      <ng-container *ngTemplateOutlet="targets; context: { key: 'apiTargets' }"></ng-container>
      <p class="bss-hint">
        A response shaped <code>{{ '{ results: [{ componentId, result }] }' }}</code> updates the ticked widgets.
        Body tokens: <code>{{ '{componentId}' }}</code> <code>{{ '{label}' }}</code> <code>{{ '{timestamp}' }}</code> <code>{{ '{filters}' }}</code>.
      </p>
    </ng-container>

    <!-- Approval -------------------------------------------------------- -->
    <ng-container *ngSwitchCase="'approval'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['approval'] }"></ng-container>
      <ng-container *ngIf="approvalFromTable(); else sliderApproval">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['approvalTable'] }"></ng-container>
        <p class="bss-hint">
          Sends one item per row: <code>{{ '{ "userId", "items": [{ "uuid", "value" }, …] }' }}</code>.
          <b>uuid</b> is the row's <b>ID column</b> (add it to the table and hide it if you like), <b>value</b> the row's
          <b>Value column</b> - what the user typed when they edited it. With <b>Only rows I edited</b>, a row is sent
          when any of its cells was changed.
        </p>
        <p class="bss-hint">
          For a custom body, use <code>{{ '{items}' }}</code> (the array) and <code>{{ '{userEmail}' }}</code>; leave the body as it is to use the shape above.
        </p>
        <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'approvalBody', label: 'Request body (optional)', value: config().approvalBody }"></ng-container>
      </ng-container>
      <ng-template #sliderApproval>
        <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'approvalBody', label: 'Request body', value: config().approvalBody }"></ng-container>
        <p class="bss-hint">
          Clicking the button POSTs this body to the approval API. Tokens:
          <code>{{ '{value}' }}</code> the slider handle's value,
          <code>{{ '{recordId}' }}</code> the record being approved (the slider's <b>Record ID column</b> in its Data tab),
          <code>{{ '{userEmail}' }}</code> the signed-in user.
        </p>
        <p class="bss-hint" *ngIf="!sliderOptions().length && !approvalTableOptions().length">Add a zone slider or a table to the page - it gives the values.</p>
      </ng-template>
      <ng-container *ngIf="config().approvalNavigate">
        <div class="bss-subhead">After approving, go to</div>
        <ng-container *ngTemplateOutlet="navigateFields"></ng-container>
        <p class="bss-hint">Runs only when the approval API answers OK. Navigation runs in Preview and on the published dashboard.</p>
      </ng-container>
    </ng-container>

    <!-- Export (the same export a nested table's Export button runs) -------- -->
    <ng-container *ngSwitchCase="'export'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['export'] }"></ng-container>
      <p class="bss-hint" *ngIf="config().exportWidgetId && !exportDataset()">Generate that component first - its dataset is what gets exported.</p>
      <p class="bss-hint" *ngIf="exportDataset()">Exports <b>{{ exportDataset() }}</b>.</p>

      <div class="bss-field">
        <label>Filter columns</label>
        <ng-select
          class="ss-select"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="exportColumns()"
          [multiple]="true"
          [closeOnSelect]="false"
          [clearable]="true"
          [searchable]="true"
          [loading]="exportColumnsLoading()"
          [disabled]="!exportDataset()"
          [placeholder]="exportDataset() ? 'None = every dashboard selection' : 'Pick the component first'"
          [ngModel]="config().exportFilterColumns"
          (ngModelChange)="set('exportFilterColumns', $event || [])"
        ></ng-select>
      </div>
      <p class="bss-hint">
        Each column is sent as a filter with the values in the component's rows, else what is selected for it on the dashboard
        (one value <code>=</code>, several <code>in</code>); a column with no value is skipped. None picked = every dashboard
        selection on the dataset's columns.
      </p>

      <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'exportBody', label: 'Body (JSON)', value: config().exportBody }"></ng-container>
      <p class="bss-hint">
        Tokens: <code>{{ '{entity}' }}</code> the component's dataset, <code>{{ '{filters}' }}</code> the filter list,
        <code>{{ '{fileName}' }}</code>, <code>{{ '{sheetName}' }}</code>, <code>{{ '{userEmail}' }}</code>. The .xlsx in the response is downloaded.
      </p>
    </ng-container>

    <!-- Filter ------------------------------------------------------------ -->
    <ng-container *ngSwitchCase="'filter'">
      <div class="bss-subhead">Targets</div>
      <ng-container *ngTemplateOutlet="targets; context: { key: 'filterTargets' }"></ng-container>

      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['filter'] }"></ng-container>

      <div class="bss-subhead">Custom API <small>optional</small></div>
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['filterApi'] }"></ng-container>
      <ng-container *ngIf="config().filterApiUrl.trim() && config().filterApiMethod !== 'GET'">
        <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'filterApiBody', label: 'Body', value: config().filterApiBody }"></ng-container>
      </ng-container>
      <p class="bss-hint">
        On click the targets recompute with <b>field = target value</b>, the same call a dropdown filter makes.
        Body tokens: <code>{{ '{value}' }}</code> <code>{{ '{field}' }}</code> <code>{{ '{componentId}' }}</code> <code>{{ '{targets}' }}</code>.
      </p>
    </ng-container>

    <!-- Save changes: a table's edited rows, or the dashboard's values ---- -->
    <ng-container *ngSwitchCase="'saveTable'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['saveSource'] }"></ng-container>

      <ng-container *ngIf="!savesValues(); else saveValues">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['saveTable'] }"></ng-container>
        <p class="bss-hint">Saves every row edited in that table, exactly as the table's own row Save does (its Save API, business keys and mandatory columns).</p>
      </ng-container>

      <ng-template #saveValues>
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['saveValues'] }"></ng-container>

        <div class="bss-field">
          <label>Entity</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel"
            [items]="datasets()" [loading]="datasetsLoading()" [addTag]="true" [clearable]="true" [searchable]="true"
            placeholder="Select an entity"
            [ngModel]="config().saveEntity || null" (ngModelChange)="set('saveEntity', $event || '')"></ng-select>
        </div>

        <div class="bss-field">
          <label>Business keys</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel"
            [items]="saveColumnsOfEntity()" [loading]="saveColumnsLoading()" [multiple]="true" [closeOnSelect]="false"
            [addTag]="true" [clearable]="true" [searchable]="true" placeholder="Select the business keys"
            [ngModel]="config().saveBusinessKeys" (ngModelChange)="set('saveBusinessKeys', $event || [])"></ng-select>
        </div>
        <p class="bss-hint">Each key is sent with the value the dashboard is on for it (a dropdown pick, the screen it was opened from) - for a button in a table row, that row's value.</p>

        <div class="bss-field">
          <label>Columns to update</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel"
            [items]="saveColumnsOfEntity()" [loading]="saveColumnsLoading()" [multiple]="true" [closeOnSelect]="false"
            [addTag]="true" [clearable]="true" [searchable]="true" placeholder="Select the columns to update"
            [ngModel]="saveColumnNames()" (ngModelChange)="setSaveColumnNames($event || [])"></ng-select>
        </div>
        <div class="bss-card" *ngFor="let entry of config().saveColumns; let i = index">
          <div class="bss-card-head">
            <span class="bss-card-name">{{ entry.column }}</span>
            <button type="button" class="bss-icon is-danger" (click)="removeSaveColumn(i)" aria-label="Remove column"><mat-icon>close</mat-icon></button>
          </div>
          <div class="bss-field">
            <label>Value from</label>
            <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="valueSourceItems()" bindLabel="label" bindValue="value" [clearable]="false" [searchable]="true"
              [ngModel]="entry.sourceId" (ngModelChange)="setSaveColumn(i, { sourceId: $event || '' })"></ng-select>
          </div>
        </div>
        <p class="bss-hint">
          Sends the same update an input box's Save sends:
          <code>{{ '{ requestData: { operation: "update", businessKey, ...keys, ...columns }, entity, … }' }}</code>.
          Each column's value is what its widget shows when the button is clicked (an input box's value, a zone slider's handle,
          a dropdown's pick, a text's number). <b>Auto</b> uses the input box / zone slider saving or filled from that column, else
          the dropdown or input on that field, else a text showing that column.
        </p>
      </ng-template>
    </ng-container>

    <!-- Upload ------------------------------------------------------------ -->
    <ng-container *ngSwitchCase="'upload'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['upload'] }"></ng-container>
      <ng-container *ngIf="config().uploadMode !== 'multipart'; else multipartUpload">
        <div class="bss-field">
          <label>Entity</label>
          <ng-select
            class="ss-select"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="uploadEntities()"
            [loading]="uploadEntitiesLoading()"
            [addTag]="true"
            [clearable]="true"
            [searchable]="true"
            placeholder="Select the entity"
            [ngModel]="config().uploadEntity || null"
            (ngModelChange)="set('uploadEntity', $event || ''); set('uploadKeyColumns', [])"
          ></ng-select>
        </div>
        <div class="bss-field">
          <label>Business keys</label>
          <ng-select
            class="ss-select"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="uploadColumns()"
            [multiple]="true"
            [addTag]="true"
            [closeOnSelect]="false"
            [clearable]="true"
            [searchable]="true"
            [loading]="uploadColumnsLoading()"
            [disabled]="!config().uploadEntity"
            [placeholder]="config().uploadEntity ? 'Select the business keys' : 'Pick the entity first'"
            [ngModel]="config().uploadKeyColumns"
            (ngModelChange)="set('uploadKeyColumns', $event || [])"
          ></ng-select>
        </div>
        <p class="bss-hint">
          Each key is sent with its value - taken from the clicked table row (button in a table), else what is selected for
          that column on the dashboard. The file goes in as bytes.
        </p>
        <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'uploadBody', label: 'Body (JSON)', value: config().uploadBody }"></ng-container>
        <p class="bss-hint">
          <code>{{ '{entity}' }}</code> = the entity, <code>{{ '{businessKeys}' }}</code> = the keys with their values,
          <code>{{ '{fileBytes}' }}</code> = the picked file's bytes (no quotes around it),
          <code>{{ '{fileBase64}' }}</code> = the bytes as base64 text, <code>{{ '{fileName}' }}</code> <code>{{ '{fileType}' }}</code>
          <code>{{ '{contentType}' }}</code> (MIME type) <code>{{ '{fileSize}' }}</code> <code>{{ '{userEmail}' }}</code>. Any dashboard selection by its column, e.g.
          <code>{{ '{cust_spec_no}' }}</code>. The platform envelope (<code>entity</code>, <code>workflowId</code>,
          <code>endpoints</code> …) is added around <code>requestData</code> automatically.
        </p>
      </ng-container>
      <ng-template #multipartUpload>
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['uploadMultipart'] }"></ng-container>
        <ng-container *ngTemplateOutlet="jsonArea; context: { key: 'uploadExtraFields', label: 'Extra form fields (JSON, optional)', value: config().uploadExtraFields }"></ng-container>
        <p class="bss-hint">The file is POSTed as <code>multipart/form-data</code> under the field name above.</p>
      </ng-template>
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['uploadCommon'] }"></ng-container>
    </ng-container>

    <!-- Navigate ---------------------------------------------------------- -->
    <ng-container *ngSwitchCase="'navigate'">
      <ng-container *ngTemplateOutlet="navigateFields"></ng-container>
      <p class="bss-hint">Navigation runs in Preview and on the published dashboard, never while you edit.</p>
    </ng-container>

    <!-- Popup ------------------------------------------------------------- -->
    <ng-container *ngSwitchCase="'popup'">
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupSource'] }"></ng-container>

      <ng-container *ngIf="config().popupSource === 'screen'">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupScreen'] }"></ng-container>
        <ng-container [ngSwitch]="popupScreenState()">
          <button *ngSwitchCase="'none'" type="button" class="bss-add" (click)="createPopupScreen.emit()">
            <mat-icon>add_box</mat-icon> Create popup screen
          </button>
          <ng-container *ngSwitchDefault>
            <button type="button" class="bss-add" (click)="openPopupScreen.emit()">
              <mat-icon>edit</mat-icon> Update popup screen
            </button>
            <button *ngIf="popupScreenState() === 'shown'" type="button" class="bss-add" (click)="hidePopupScreen.emit()">
              <mat-icon>check</mat-icon> Done — hide popup screen
            </button>
          </ng-container>
        </ng-container>
        <p class="bss-hint">
          A popup screen is a Frame on the canvas: add text, inputs, file upload, buttons — any layer — then
          press <b>Done</b> to hide it and Save. Resizing the frame rescales everything inside.
          Give a button inside it the <b>Close popup</b> feature to close it. Popup screens are not shown as page tabs.
        </p>
      </ng-container>

      <ng-container *ngIf="config().popupSource === 'widget'; else popupNotWidget">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupWidget'] }"></ng-container>
        <p class="bss-hint">
          {{ popupOptions().length ? 'Shows that Popup widget\'s content — build it with its own Data form.' : 'Add a Popup widget to the canvas first, or write the content here.' }}
        </p>
      </ng-container>
      <ng-template #popupNotWidget><ng-container *ngIf="config().popupSource === 'inline'">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupInline'] }"></ng-container>
        <ng-container *ngIf="config().popupSize === 'square'">
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupSquare'] }"></ng-container>
        </ng-container>
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupHeading'] }"></ng-container>
        <label class="dsg-field dsg-field-wide">
          <span>Message</span>
          <textarea rows="4" placeholder="Shown in the popup. A blank line starts a new paragraph." [ngModel]="config().popupMessage" (ngModelChange)="set('popupMessage', $event)"></textarea>
        </label>
        <p class="bss-hint">Box style</p>
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['popupStyle'] }"></ng-container>
      </ng-container></ng-template>
    </ng-container>

    <!-- Close popup ------------------------------------------------------- -->
    <p class="bss-hint" *ngSwitchCase="'closePopup'">Closes the popup screen this button sits in.</p>

    <app-design-control-renderer
      [control]="confirm"
      [values]="appearance()"
      [options]="allOptions()"
      (valueChange)="onControlChange($event)"
    ></app-design-control-renderer>
  </div>
</div>

<p class="bss-hint is-note" *ngIf="config().action === 'none'">
  Pick a <b>Feature</b> to make the button download, call APIs, filter, navigate or open a popup.
</p>

<!-- A list of flat controls. -->
<ng-template #flat let-list>
  <app-design-control-renderer
    *ngFor="let control of list; trackBy: trackByControlKey"
    [control]="control"
    [values]="appearance()"
    [options]="allOptions()"
    (valueChange)="onControlChange($event)"
  ></app-design-control-renderer>
</ng-template>

<!-- A JSON request body. -->
<ng-template #jsonArea let-key="key" let-label="label" let-value="value">
  <label class="dsg-field dsg-field-wide" [class.is-invalid]="isBadJson(value)">
    <span>{{ label }}</span>
    <textarea rows="4" spellcheck="false" placeholder='{ "id": "{componentId}" }' [ngModel]="value" (ngModelChange)="set(key, $event)"></textarea>
  </label>
</ng-template>

<!-- Checklist of the generated widgets. -->
<ng-template #targets let-key="key">
  <div class="bss-targets" *ngIf="targetOptions().length; else noTargets">
    <label class="dsg-check" *ngFor="let target of targetOptions(); trackBy: trackByValue">
      <input type="checkbox" [checked]="isTarget(key, $any(target.value))" (change)="toggleTarget(key, $any(target.value), $any($event.target).checked)" />
      <span>{{ target.label }}</span>
    </label>
  </div>
  <ng-template #noTargets>
    <p class="bss-hint">No targets yet. Generate a chart or table in the <b>Data</b> tab and it will show up here.</p>
  </ng-template>
</ng-template>

<!-- Where to go: shared by the Navigate action and Approval's "Then navigate". -->
<ng-template #navigateFields>
      <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateType'] }"></ng-container>

      <ng-container [ngSwitch]="config().navigateType">
        <ng-container *ngSwitchCase="'dashboard'">
          <label class="dsg-field dsg-field-wide">
            <span>Dashboard</span>
            <input
              type="text"
              list="bss-dashboards"
              placeholder="Published dashboard slug"
              [ngModel]="config().navigateTarget"
              (ngModelChange)="set('navigateTarget', $event)"
            />
          </label>
          <datalist id="bss-dashboards">
            <option *ngFor="let dashboard of dashboardOptions(); trackBy: trackByValue" [value]="dashboard.value">{{ dashboard.label }}</option>
          </datalist>
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateFilter'] }"></ng-container>
        </ng-container>

        <ng-container *ngSwitchCase="'screen'">
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateScreen'] }"></ng-container>
          <p class="bss-hint">Switches the published dashboard (or Preview) to that screen.</p>
        </ng-container>

        <ng-container *ngSwitchCase="'route'">
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateRoute'] }"></ng-container>
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateFilter'] }"></ng-container>
        </ng-container>

        <ng-container *ngSwitchCase="'url'">
          <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateUrl'] }"></ng-container>
        </ng-container>

        <p class="bss-hint" *ngSwitchCase="'landing'">Goes to the app's landing page.</p>
      </ng-container>

      <ng-container *ngIf="config().navigateType !== 'screen'">
        <ng-container *ngTemplateOutlet="flat; context: { $implicit: controls['navigateTab'] }"></ng-container>
      </ng-container>
</ng-template>
